// DoorTypesSection.jsx
// Phase 1: Door Types Section - Extracted from ProjectDetail.js
// State hala ProjectDetail.js'de - bu component sadece props alıyor

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Upload, Edit2, Loader2, ClipboardPaste, Image as ImageIcon } from 'lucide-react';
import ColorPickerPopover from '@/components/ColorPickerPopover';

export const DoorTypesSection = ({
  // Data
  filteredDoorTypes,
  selectedDoorTypeIds,
  canEditCSIItems,
  doorTypePictureUploading,
  operationLoading,
  API,
  // Handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onAddNew,
  onEdit,
  onDelete,
  onPictureUpload,
  onPasteFromClipboard,
  onDeletePicture,
  onViewDrawing,
  // Color picker handlers
  onColorPickerOpenChange,
  onColorChange,
  onColorSave,
}) => {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Door Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && filteredDoorTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={onSelectAll}
              >
                {selectedDoorTypeIds.length === filteredDoorTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={onConfirmBulkDelete}
                disabled={selectedDoorTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedDoorTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Button onClick={onAddNew} data-testid="add-door-type-btn" disabled={operationLoading}>
              {operationLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Door Type
            </Button>
          )}
        </div>
      </div>

      {filteredDoorTypes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">No door types created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredDoorTypes.map((doorType) => (
            <Card key={doorType.id} data-testid={`door-type-${doorType.id}`} className={selectedDoorTypeIds.includes(doorType.id) ? 'ring-2 ring-blue-500' : ''}>
              <div className="flex">
                {/* Checkbox column */}
                {canEditCSIItems && (
                  <div className="w-12 flex-shrink-0 flex items-start justify-center pt-4 border-r">
                    <Checkbox
                      checked={selectedDoorTypeIds.includes(doorType.id)}
                      onCheckedChange={(checked) => onToggleSelection(doorType.id, { target: { checked } })}
                      className="h-5 w-5"
                    />
                  </div>
                )}
                
                {/* Picture column - Left */}
                <div className="flex-shrink-0 p-4 border-r" style={{ width: '250px', height: '300px' }}>
                  {(doorType.pictureCloudinary?.urls?.medium || doorType.pictureUrl) ? (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        <div className="flex gap-1">
                          {canEditCSIItems && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPasteFromClipboard(doorType.id)}
                                title="Paste image from clipboard"
                                disabled={doorTypePictureUploading === doorType.id}
                              >
                                {doorTypePictureUploading === doorType.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ClipboardPaste className="h-3 w-3" />
                                )}
                              </Button>
                              <label className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild>
                                  <span><Upload className="h-3 w-3" /></span>
                                </Button>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/png,image/jpeg,image/jpg,application/pdf"
                                  onChange={(e) => onPictureUpload(e, doorType.id)}
                                />
                              </label>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => onDeletePicture(doorType.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="flex-1 cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center overflow-hidden"
                        onClick={() => onViewDrawing({
                          url: doorType.pictureCloudinary?.urls?.large || `${API}${doorType.pictureUrl}?t=${doorType.updatedAt || doorType.createdAt || 'default'}`,
                          title: `${doorType.name} - Picture`
                        })}
                      >
                        <img
                          src={doorType.pictureCloudinary?.urls?.large || `${API}${doorType.pictureUrl}?t=${doorType.updatedAt || doorType.createdAt || 'default'}`}
                          alt="Door Type Picture"
                          className="max-w-full max-h-full object-contain border rounded"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        {canEditCSIItems && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onPasteFromClipboard(doorType.id)}
                              title="Paste image from clipboard"
                              disabled={doorTypePictureUploading === doorType.id}
                            >
                              {doorTypePictureUploading === doorType.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ClipboardPaste className="h-3 w-3" />
                              )}
                            </Button>
                            <label className="cursor-pointer">
                              <Button variant="outline" size="sm" asChild>
                                <span><Upload className="h-3 w-3" /></span>
                              </Button>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/png,image/jpeg,image/jpg,application/pdf"
                                onChange={(e) => onPictureUpload(e, doorType.id)}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        {canEditCSIItems ? (
                          <label className="cursor-pointer w-full">
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
                              <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                              <p className="text-sm text-slate-600">Upload Picture</p>
                              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/png,image/jpeg,image/jpg,application/pdf"
                              onChange={(e) => onPictureUpload(e, doorType.id)}
                            />
                          </label>
                        ) : (
                          <div className="text-center text-slate-400">
                            <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-sm">No Picture</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Content column - Right */}
                <div className="flex-1 p-4 flex flex-col justify-between" style={{ height: '300px' }}>
                  {/* Action buttons */}
                  {canEditCSIItems && (
                    <div className="flex gap-2 items-center justify-end mb-2">
                      <ColorPickerPopover
                        id={`door-${doorType.id}`}
                        currentColor={doorType.markerColor}
                        pendingColor={doorType.pendingMarkerColor}
                        isOpen={doorType.colorPickerOpen}
                        isSaving={doorType.savingColor}
                        onOpenChange={(open) => onColorPickerOpenChange(doorType.id, open)}
                        onColorChange={(newColor) => onColorChange(doorType.id, newColor)}
                        onSave={() => onColorSave(doorType)}
                      />
                      <Button variant="outline" size="sm" onClick={() => onEdit(doorType)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => onDelete(doorType.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* 4 Rows of content */}
                  <div className="flex-1 flex flex-col justify-evenly text-sm">
                    {/* Row 1: Type, Manufacturer, Model */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Type</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.name}</div>
                      <div className="font-semibold w-[95px]">Manufacturer</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.manufacturer || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Model</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.model || 'N/A'}</div>
                    </div>
                    
                    {/* Row 2: Door Style, Material, Operation, Fire Rating */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Door Style</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.doorType || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Material</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.doorMaterial || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Operation</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.operationType || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Fire Rating</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.fireRated !== undefined && doorType.fireRated !== null ? (doorType.fireRated === 0 ? 'No' : `${doorType.fireRated} min`) : 'N/A'}</div>
                    </div>
                    
                    {/* Row 3: Finishes */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Finish Type</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.finishType || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Interior Finish</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.finishInterior || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Exterior Finish</div>
                      <div className="w-[10px]">:</div>
                      <div className="flex-1">{doorType.finishExterior || 'N/A'}</div>
                    </div>
                    
                    {/* Row 4: Warranty Periods */}
                    <div className="flex">
                      <div className="font-semibold w-[95px]">Frame Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.frameWarrantyYears ? `${doorType.frameWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Finish Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.finishWarrantyYears ? `${doorType.finishWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Hardware Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.hardwareWarrantyYears ? `${doorType.hardwareWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Installation Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{doorType.installationWarrantyYears ? `${doorType.installationWarrantyYears} Years` : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DoorTypesSection;
